import React from 'react'
import { Box, Grid, Slider, TextField, Typography } from '@mui/material'
import { FORM_INPUT_WIDTH } from '../../Config/layoutConstants'

interface SliderWithInputProps {
  /** Display label for the field */
  label: string
  /** Form field name */
  name: string
  /** Current value */
  value: number
  /** Minimum allowed value */
  min: number
  /** Maximum allowed value */
  max: number
  /** Step increment */
  step: number
  /** ID prefix for the input elements */
  id: string
  /** Handler for slider changes */
  onSliderChange: (e: Event, value: number, activeThumb: number) => void
  /** Handler for text input changes */
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * A combined slider and number input field for numeric values.
 * Provides both visual slider control and precise numeric input.
 */
export const SliderWithInput: React.FC<SliderWithInputProps> = ({
  label,
  name,
  value,
  min,
  max,
  step,
  id,
  onSliderChange,
  onInputChange,
}) => {
  return (
    <Box>
      <Typography
        variant='subtitle2'
        component='div'
        align='left'
        sx={{ fontWeight: 400, color: 'text.secondary' }}
      >
        {label}
      </Typography>
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid size="grow" sx={{ width: '100%' }}>
          <Slider
            size="medium"
            value={value}
            valueLabelDisplay="auto"
            step={step}
            min={min}
            max={max}
            name={name}
            id={`${id}Slider`}
            onChange={onSliderChange}
            aria-label={label}
          />
        </Grid>
        <Grid sx={{ width: (theme) => theme.spacing(FORM_INPUT_WIDTH) }}>
          <TextField
            id={`${id}Input`}
            name={name}
            value={value}
            type="number"
            onChange={onInputChange}
            slotProps={{ 
              htmlInput: { 
                min, 
                max, 
                step,
                'aria-label': label,
              } 
            }}
          />
        </Grid>
      </Grid>
    </Box>
  )
}

export default SliderWithInput
